/**
 * RolePermissionsPage.jsx
 * Place in: frontend/src/components/setup/RolePermissionsPage.jsx
 *
 * - Roles fetched from API (not hardcoded)
 * - Clicking a role loads its current permissions
 * - Checkboxes update local state
 * - "Save Permissions" calls PUT /permissions/roles/{id}
 */
import { useState, useEffect } from 'react';
import { permissionsAPI } from '../../api';
import toast from 'react-hot-toast';

/* ── Module display labels ────────────────────────────────── */
const MODULE_LABELS = {
  dashboard:   { label: 'Dashboard',       icon: '📊' },
  assets:      { label: 'Asset Management',icon: '🏭' },
  work_orders: { label: 'Work Orders',     icon: '🔧' },
  spares:      { label: 'Spare Parts',     icon: '⚙'  },
  vendors:     { label: 'Vendors',         icon: '🤝' },
  users:       { label: 'User Management', icon: '👥' },
  setup:       { label: 'Setup / Masters', icon: '⚙️'  },
  reports:     { label: 'Reports',         icon: '📋' },
};

const ACTIONS = [
  { key: 'can_view',   label: 'View'   },
  { key: 'can_create', label: 'Create' },
  { key: 'can_edit',   label: 'Edit'   },
  { key: 'can_delete', label: 'Delete' },
  { key: 'can_export', label: 'Export' },
];

const BLANK_PERMS = (modules) =>
  Object.fromEntries(
    (modules || Object.keys(MODULE_LABELS)).map(m => [
      m,
      { can_view: false, can_create: false, can_edit: false, can_delete: false, can_export: false },
    ])
  );

export default function RolePermissionsPage() {
  const [roles,       setRoles]       = useState([]);   // [{id, name}]
  const [modules,     setModules]     = useState(Object.keys(MODULE_LABELS));
  const [selectedRole,setSelectedRole]= useState(null); // {id, name}
  const [perms,       setPerms]       = useState({});   // {module: {can_view, …}}
  const [loading,     setLoading]     = useState(true);
  const [loadingPerms,setLoadingPerms]= useState(false);
  const [saving,      setSaving]      = useState(false);
  const [dirty,       setDirty]       = useState(false);

  /* ── Load all roles on mount ── */
  useEffect(() => {
    setLoading(true);
    permissionsAPI.getRoles()
      .then(r => {
        const data = r.data;
        setRoles(data.roles || []);
        if (data.modules?.length) setModules(data.modules);
        // Auto-select first role
        if (data.roles?.length) {
          const first = data.roles[0];
          setSelectedRole({ id: first.id, name: first.name });
          setPerms(first.permissions || BLANK_PERMS(data.modules));
        }
      })
      .catch(() => toast.error('Failed to load roles'))
      .finally(() => setLoading(false));
  }, []);

  /* ── Load permissions when role changes ── */
  const selectRole = (role) => {
    if (dirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setSelectedRole(role);
    setDirty(false);
    setLoadingPerms(true);
    permissionsAPI.updateRole
      ? null : null; // just using getRolePerms below
    permissionsAPI.getRolePerms(role.id)
      .then(r => {
        setPerms(r.data.permissions || BLANK_PERMS(modules));
        if (r.data.modules?.length) setModules(r.data.modules);
      })
      .catch(() => toast.error('Failed to load permissions'))
      .finally(() => setLoadingPerms(false));
  };

  /* ── Toggle a single permission ── */
  const toggle = (module, action) => {
    setPerms(prev => ({
      ...prev,
      [module]: { ...prev[module], [action]: !prev[module]?.[action] },
    }));
    setDirty(true);
  };

  /* ── Toggle all actions for a module ── */
  const toggleRow = (module) => {
    const all = ACTIONS.every(a => perms[module]?.[a.key]);
    setPerms(prev => ({
      ...prev,
      [module]: Object.fromEntries(ACTIONS.map(a => [a.key, !all])),
    }));
    setDirty(true);
  };

  /* ── Toggle one action across all modules ── */
  const toggleColumn = (action) => {
    const all = modules.every(m => perms[m]?.[action]);
    setPerms(prev => {
      const next = { ...prev };
      modules.forEach(m => {
        next[m] = { ...next[m], [action]: !all };
      });
      return next;
    });
    setDirty(true);
  };

  /* ── Save ── */
  const save = async () => {
    if (!selectedRole) return;
    setSaving(true);
    try {
      const permissions = modules.map(m => ({
        module:     m,
        can_view:   Boolean(perms[m]?.can_view),
        can_create: Boolean(perms[m]?.can_create),
        can_edit:   Boolean(perms[m]?.can_edit),
        can_delete: Boolean(perms[m]?.can_delete),
        can_export: Boolean(perms[m]?.can_export),
      }));
      await permissionsAPI.updateRole(selectedRole.id, { permissions });
      toast.success(`Permissions saved for "${selectedRole.name}"`);
      setDirty(false);
    } catch(e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  /* ── Styles ── */
  const th = {
    padding: '10px 14px', fontSize: 11, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '.06em',
    color: '#64748b', background: '#f8fafc',
    borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap',
  };
  const td = { padding: '2px 14px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
      height:200, color:'#94a3b8', fontSize:14 }}>
      Loading roles…
    </div>
  );

  return (
    <div style={{ display:'flex', gap:20, alignItems:'flex-start' }}>

      {/* ── Left: Role List ── */}
      <div style={{ width:200, flexShrink:0, background:'#fff', borderRadius:12,
        border:'1px solid #e2e8f0', overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,.06)' }}>
        <div style={{ padding:'12px 14px', borderBottom:'1px solid #e2e8f0',
          fontSize:12, fontWeight:700, color:'#374151', textTransform:'uppercase',
          letterSpacing:'.06em', background:'#f8fafc' }}>
          Roles
        </div>
        {roles.length === 0 ? (
          <div style={{ padding:14, fontSize:13, color:'#94a3b8' }}>No roles found</div>
        ) : roles.map(role => (
          <button key={role.id} onClick={() => selectRole(role)}
            style={{
              width: '100%', textAlign: 'left',
              padding: '10px 14px', border: 'none',
              borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
              background: selectedRole?.id === role.id ? '#eff6ff' : '#fff',
              color:      selectedRole?.id === role.id ? '#1a56db' : '#374151',
              fontWeight: selectedRole?.id === role.id ? 700 : 400,
              fontSize: 13,
              borderLeft: selectedRole?.id === role.id
                ? '3px solid #1a56db' : '3px solid transparent',
              transition: 'all .15s',
            }}>
            {role.name}
          </button>
        ))}
      </div>

      {/* ── Right: Permissions Matrix ── */}
      <div style={{ flex:1 }}>
        {!selectedRole ? (
          <div style={{ padding:40, textAlign:'center', color:'#94a3b8', fontSize:14 }}>
            ← Select a role to configure permissions
          </div>
        ) : (
          <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e2e8f0',
            boxShadow:'0 1px 4px rgba(0,0,0,.06)', overflow:'hidden' }}>

            {/* Header */}
            <div style={{ padding:'14px 20px', borderBottom:'1px solid #e2e8f0',
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontSize:15, fontWeight:700, color:'#1a1f2e' }}>
                  Permissions — {selectedRole.name}
                </div>
                <div style={{ fontSize:12, color:'#94a3b8', marginTop:2 }}>
                  Click a header to toggle the entire column • Click a row label to toggle all actions
                </div>
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                {dirty && (
                  <span style={{ fontSize:12, color:'#d97706', fontWeight:500 }}>
                    ● Unsaved changes
                  </span>
                )}
                <button onClick={save} disabled={saving || !dirty}
                  style={{
                    padding: '8px 20px', borderRadius: 8, border: 'none',
                    background: (saving || !dirty) ? '#e2e8f0' : '#1a56db',
                    color:      (saving || !dirty) ? '#94a3b8' : '#fff',
                    fontSize: 13, fontWeight: 600,
                    cursor: (saving || !dirty) ? 'not-allowed' : 'pointer',
                    transition: 'all .15s',
                  }}>
                  {saving ? 'Saving…' : '💾 Save Permissions'}
                </button>
              </div>
            </div>

            {/* Loading overlay */}
            {loadingPerms ? (
              <div style={{ padding:40, textAlign:'center', color:'#94a3b8', fontSize:13 }}>
                Loading permissions…
              </div>
            ) : (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, textAlign:'left', width:200 }}>Module</th>
                      {ACTIONS.map(a => (
                        <th key={a.key} style={{ ...th, textAlign:'center', cursor:'pointer' }}
                          onClick={() => toggleColumn(a.key)}
                          title={`Click to toggle all "${a.label}" permissions`}>
                          {a.label}
                          <div style={{ fontSize:9, color:'#cbd5e1', fontWeight:400 }}>
                            All
                            {/* click to toggle all */}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map((mod, idx) => {
                      const mp = perms[mod] || {};
                      const allOn = ACTIONS.every(a => mp[a.key]);
                      const ml = MODULE_LABELS[mod] || { label: mod, icon: '📄' };
                      return (
                        <tr key={mod}
                          style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                          {/* Module label — click to toggle entire row */}
                          <td style={{ ...td, cursor:'pointer' }} onClick={() => toggleRow(mod)}
                            title="Click to toggle all permissions for this module">
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                              <span style={{ fontSize:16 }}>{ml.icon}</span>
                              <div>
                                <div style={{ fontSize:13, fontWeight:600, color:'#1a1f2e' }}>
                                  {ml.label}
                                </div>
                                <div style={{ fontSize:10, color:'#94a3b8' }}>
                                  {allOn ? 'All enabled' : 'Click to enable all'}
                                </div>
                              </div>
                            </div>
                          </td>
                          {/* Checkboxes */}
                          {ACTIONS.map(a => (
                            <td key={a.key} style={{ ...td, textAlign:'center' }}>
                              <label style={{ display:'inline-flex', alignItems:'center',
                                justifyContent:'center', cursor:'pointer',
                                width:36, height:36, borderRadius:8,
                                background: mp[a.key] ? '#eff6ff' : 'transparent',
                                border: `1.5px solid ${mp[a.key] ? '#1a56db' : '#e2e8f0'}`,
                                transition: 'all .15s',
                              }}>
                                <input
                                  type="checkbox"
                                  checked={Boolean(mp[a.key])}
                                  onChange={() => toggle(mod, a.key)}
                                  style={{ display:'none' }}
                                />
                                {mp[a.key]
                                  ? <span style={{ fontSize:16, lineHeight:1 }}>✓</span>
                                  : <span style={{ fontSize:16, color:'#e2e8f0', lineHeight:1 }}>−</span>
                                }
                              </label>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Footer quick actions */}
            <div style={{ padding:'10px 20px', borderTop:'1px solid #f1f5f9',
              display:'flex', gap:10, alignItems:'center' }}>
              <span style={{ fontSize:12, color:'#94a3b8', flex:1 }}>
                Quick set:
              </span>
              <button onClick={() => {
                const all = {};
                modules.forEach(m => { all[m] = Object.fromEntries(ACTIONS.map(a=>[a.key,true])); });
                setPerms(all); setDirty(true);
              }} style={{ padding:'4px 10px', borderRadius:6, border:'1px solid #e2e8f0',
                background:'#fff', fontSize:12, cursor:'pointer', color:'#374151' }}>
                ✓ Enable All
              </button>
              <button onClick={() => {
                const none = {};
                modules.forEach(m => { none[m] = Object.fromEntries(ACTIONS.map(a=>[a.key,false])); });
                setPerms(none); setDirty(true);
              }} style={{ padding:'4px 10px', borderRadius:6, border:'1px solid #e2e8f0',
                background:'#fff', fontSize:12, cursor:'pointer', color:'#374151' }}>
                ✕ Disable All
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
